// src/components/admin/PromoCodeList.tsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  PlusCircle, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar,
  RefreshCw,
  CheckCircle,
  XCircle,
  Download 
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { PromoCode } from '../../types/admin';

interface PromoCodeListProps {
  onViewPromoCode: (promoCode: PromoCode) => void;
  onCreateNew: () => void;
}

// Filter state interface
interface FilterState {
  planType: 'all' | 'medium' | 'premium';
  durationType: 'all' | 'once' | 'repeating' | 'forever';
  status: 'all' | 'active' | 'inactive';
  createdAfter: string | null;
  createdBefore: string | null;
  expiresAfter: string | null;
  expiresBefore: string | null;
  search: string;
}

// Column keys for sorting
type SortColumn = 'code' | 'planType' | 'durationType' | 'createdAt' | 'expiresAt' | 'redemptionCount' | 'isActive';

const PromoCodeList: React.FC<PromoCodeListProps> = ({ onViewPromoCode, onCreateNew }) => {
  // State for promo codes
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    planType: 'all',
    durationType: 'all',
    status: 'all',
    createdAfter: null,
    createdBefore: null,
    expiresAfter: null,
    expiresBefore: null,
    search: ''
  });
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Show/hide filters
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection state for batch operations
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Fetch promo codes with current filters, pagination, and sorting
  const fetchPromoCodes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Convert filters to query params
      const params: Record<string, any> = {
        page,
        limit,
      };
      
      // Add filters
      if (filters.planType !== 'all') params.planType = filters.planType;
      if (filters.durationType !== 'all') params.durationType = filters.durationType;
      if (filters.status !== 'all') params.isActive = filters.status === 'active';
      if (filters.search) params.search = filters.search;
      if (filters.createdAfter) params.createdAfter = filters.createdAfter;
      if (filters.createdBefore) params.createdBefore = filters.createdBefore;
      if (filters.expiresAfter) params.expiresAfter = filters.expiresAfter;
      if (filters.expiresBefore) params.expiresBefore = filters.expiresBefore;
      
      // Add sorting
      params.sortBy = sortColumn;
      params.sortOrder = sortDirection;
      
      const result = await adminApi.getPromoCodes(params);
      
      setPromoCodes(result.data);
      setTotal(result.meta.total);
      
      // Reset selection when list changes
      setSelectedCodes([]);
      setSelectAll(false);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch promo codes');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchPromoCodes();
  }, [page, limit, sortColumn, sortDirection]);
  
  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPromoCodes();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [filters]);
  
  // Handle sort column change
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (
    key: keyof FilterState,
    value: string | boolean | null
  ) => {
    setFilters({
      ...filters,
      [key]: value
    });
    
    // Reset to first page when filters change
    setPage(1);
  };
  
  // Handle checkbox selection
  const handleSelect = (codeId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedCodes([...selectedCodes, codeId]);
    } else {
      setSelectedCodes(selectedCodes.filter(id => id !== codeId));
    }
  };
  
  // Handle select all toggle
  const handleSelectAll = (isSelected: boolean) => {
    setSelectAll(isSelected);
    if (isSelected) {
      setSelectedCodes(promoCodes.map(code => code.id));
    } else {
      setSelectedCodes([]);
    }
  };
  
  // Handle batch deactivation
  const handleBatchDeactivate = async () => {
    if (!selectedCodes.length) return;
    
    if (!window.confirm(`Are you sure you want to deactivate ${selectedCodes.length} selected promo codes?`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // In a real implementation, you would call a batch operation API
      // For now, we'll simulate by updating each code individually
      await Promise.all(
        selectedCodes.map(id => adminApi.updatePromoCode(id, { isActive: false }))
      );
      
      // Refresh the list
      fetchPromoCodes();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate promo codes');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle exporting selected codes
  const handleExportSelected = () => {
    if (!selectedCodes.length) return;
    
    // Filter down to just the selected codes
    const selectedData = promoCodes.filter(code => 
      selectedCodes.includes(code.id)
    );
    
    // Create CSV content
    let csv = 'Code,Plan Type,Duration Type,Duration Months,Expires At,Max Redemptions,Redemption Count,Is Active,Created At\n';
    
    selectedData.forEach(code => {
      csv += [
        code.code,
        code.planType,
        code.durationType,
        code.durationMonths || '',
        code.expiresAt || '',
        code.maxRedemptions || '',
        code.redemptionCount,
        code.isActive ? 'true' : 'false',
        new Date(code.createdAt).toISOString()
      ].join(',') + '\n';
    });
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo-codes-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Handle deactivating a single code
  const handleDeactivate = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this promo code?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      await adminApi.updatePromoCode(id, { isActive: false });
      fetchPromoCodes();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate promo code');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle activating a single code
  const handleActivate = async (id: string) => {
    try {
      setIsLoading(true);
      await adminApi.updatePromoCode(id, { isActive: true });
      fetchPromoCodes();
    } catch (err: any) {
      setError(err.message || 'Failed to activate promo code');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle deleting a code
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this promo code? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      await adminApi.deletePromoCode(id);
      fetchPromoCodes();
    } catch (err: any) {
      setError(err.message || 'Failed to delete promo code');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Calculate total pages
  const totalPages = Math.ceil(total / limit);
  
  // Render status badge
  const renderStatusBadge = (code: PromoCode) => {
    if (!code.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
    
    if (code.expiresAt && new Date(code.expiresAt) < new Date()) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Calendar className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    }
    
    if (code.maxRedemptions && code.redemptionCount >= code.maxRedemptions) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Fully Redeemed
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Promo Codes</h2>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onCreateNew}
            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            New Promo Code
          </button>
          {selectedCodes.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleBatchDeactivate}
                className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={isLoading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Deactivate Selected
              </button>
              <button
                type="button"
                onClick={handleExportSelected}
                className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Selected
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Search and filter bar */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by code or description..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filters.planType}
              onChange={e => handleFilterChange('planType', e.target.value)}
              className="border rounded-md py-2 px-3"
            >
              <option value="all">All Plans</option>
              <option value="medium">Medium</option>
              <option value="premium">Premium</option>
            </select>
            <select
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              className="border rounded-md py-2 px-3"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 border rounded-md hover:bg-gray-50"
            >
              <Filter className="h-5 w-5 mr-1" />
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </button>
            <button
              type="button"
              onClick={fetchPromoCodes}
              className="p-2 text-gray-600 border rounded-md hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Additional filters */}
        {showFilters && (
          <div className="mt-4 p-4 border rounded-md bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration Type
                </label>
                <select
                  value={filters.durationType}
                  onChange={e => handleFilterChange('durationType', e.target.value)}
                  className="w-full border rounded-md py-2 px-3"
                >
                  <option value="all">All Duration Types</option>
                  <option value="once">One-time</option>
                  <option value="repeating">Recurring</option>
                  <option value="forever">Permanent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created Date Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.createdAfter || ''}
                    onChange={e => handleFilterChange('createdAfter', e.target.value || null)}
                    className="flex-1 border rounded-md py-2 px-3"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filters.createdBefore || ''}
                    onChange={e => handleFilterChange('createdBefore', e.target.value || null)}
                    className="flex-1 border rounded-md py-2 px-3"
                    placeholder="To"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.expiresAfter || ''}
                    onChange={e => handleFilterChange('expiresAfter', e.target.value || null)}
                    className="flex-1 border rounded-md py-2 px-3"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filters.expiresBefore || ''}
                    onChange={e => handleFilterChange('expiresBefore', e.target.value || null)}
                    className="flex-1 border rounded-md py-2 px-3"
                    placeholder="To"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      planType: 'all',
                      durationType: 'all',
                      status: 'all',
                      createdAfter: null,
                      createdBefore: null,
                      expiresAfter: null,
                      expiresBefore: null,
                      search: ''
                    });
                    setPage(1);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          {error}
        </div>
      )}
      
      {/* Main table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={e => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('code')}
              >
                <div className="flex items-center">
                  Code
                  {sortColumn === 'code' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('planType')}
              >
                <div className="flex items-center">
                  Plan
                  {sortColumn === 'planType' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('durationType')}
              >
                <div className="flex items-center">
                  Duration
                  {sortColumn === 'durationType' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('redemptionCount')}
              >
                <div className="flex items-center">
                  Usage
                  {sortColumn === 'redemptionCount' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('isActive')}
              >
                <div className="flex items-center">
                  Status
                  {sortColumn === 'isActive' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center">
                  Created
                  {sortColumn === 'createdAt' && (
                    sortDirection === 'asc' ? 
                      <ChevronUp className="h-4 w-4 ml-1" /> : 
                      <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : promoCodes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                  No promo codes found
                </td>
              </tr>
            ) : (
              promoCodes.map(code => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedCodes.includes(code.id)}
                      onChange={e => handleSelect(code.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 font-mono">
                      {code.code}
                    </div>
                    {code.description && (
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
                        {code.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {code.planType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 capitalize">
                      {code.durationType}
                    </div>
                    {code.durationType !== 'forever' && code.durationMonths && (
                      <div className="text-xs text-gray-500">
                        {code.durationMonths} month{code.durationMonths !== 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {code.redemptionCount} {code.maxRedemptions ? `/ ${code.maxRedemptions}` : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderStatusBadge(code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(code.createdAt).toLocaleDateString()}
                    </div>
                    {code.expiresAt && (
                      <div className="text-xs text-gray-500">
                        Expires: {new Date(code.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => onViewPromoCode(code)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => code.isActive ? handleDeactivate(code.id) : handleActivate(code.id)}
                        className={code.isActive ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                        title={code.isActive ? "Deactivate" : "Activate"}
                      >
                        {code.isActive ? <XCircle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{promoCodes.length}</span> of{' '}
          <span className="font-medium">{total}</span> results
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={limit}
            onChange={e => {
              setLimit(Number(e.target.value));
              setPage(1); // Reset to first page when changing limit
            }}
            className="border rounded-md py-1 px-2 text-sm"
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
          
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <span className="px-4 py-1 text-sm">
            Page <span className="font-medium">{page}</span> of{' '}
            <span className="font-medium">{totalPages || 1}</span>
          </span>
          
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeList;